/**
 * ActionGuidanceModal — modal naprowadzający dla akcji zbramkowanych brakiem konfiguracji
 * (PHASE 25 Faza 2). Zamiast suchego „nie udało się" po kliknięciu „Wyślij do KSeF" /
 * „Dekretuj" bez konfiguracji, pokazuje checklistę gotowości (co jest / czego brakuje) i
 * skróty do właściwych ekranów Ustawień. Spełnione warunki znikają jako „gotowe" (bez linku),
 * zostają tylko brakujące ze skrótami — modal sam się kurczy.
 */

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { ListActionId } from '@saas/shared';
import { ArrowRight, CircleCheck, CircleX, Code, FileText, Landmark, Send } from 'lucide-react';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import type { ActionReadiness } from '@/lib/list-actions/use-action-readiness';

interface Props {
  open: boolean;
  actionId: ListActionId | null;
  readiness: ActionReadiness;
  onClose: () => void;
}

interface ReqLink {
  icon?: React.ReactNode;
  label: string;
  path: string;
}

export function ActionGuidanceModal({
  open,
  actionId,
  readiness,
  onClose,
}: Props): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  const isKsef = actionId === 'ksef_send';
  const isFk = actionId === 'fk_send';
  const titleKey = isKsef
    ? 'tenant.listActions.gate.ksefTitle'
    : isFk
      ? 'tenant.listActions.gate.fkTitle'
      : 'tenant.listActions.gate.decreeTitle';
  const subtitleKey = isKsef
    ? 'tenant.listActions.gate.ksefSubtitle'
    : isFk
      ? 'tenant.listActions.gate.fkSubtitle'
      : 'tenant.listActions.gate.decreeSubtitle';
  const title = t(titleKey);
  const subtitle = t(subtitleKey);

  return (
    <Modal open={open} onClose={onClose} size="md" title={title} description={subtitle}>
      <div className="flex flex-col divide-y divide-border-secondary">
        {isKsef ? (
          <Requirement
            met={readiness.ksefConfigured}
            label={t('tenant.listActions.gate.ksefReq')}
            onGo={go}
            links={[
              {
                icon: <Send className="h-4 w-4" aria-hidden="true" />,
                label: t('tenant.listActions.gate.ksefLink'),
                path: '/settings/ksef',
              },
            ]}
          />
        ) : isFk ? (
          <Requirement
            met={readiness.fkConfigured}
            label={t('tenant.listActions.gate.fkReq')}
            onGo={go}
            links={[
              {
                icon: <Landmark className="h-4 w-4" aria-hidden="true" />,
                label: t('tenant.listActions.gate.fkLink'),
                path: '/settings/integrations',
              },
            ]}
          />
        ) : (
          <>
            <Requirement
              met={readiness.hasChart}
              label={t('tenant.listActions.gate.reqChart')}
              onGo={go}
              links={[
                {
                  label: t('tenant.listActions.gate.chartLink'),
                  path: '/settings/chart-of-accounts',
                },
              ]}
            />
            <Requirement
              met={readiness.hasDecreeRule}
              label={t('tenant.listActions.gate.reqRule')}
              hint={t('tenant.listActions.gate.reqRuleHint')}
              onGo={go}
              links={[
                {
                  icon: <FileText className="h-4 w-4" aria-hidden="true" />,
                  label: t('tenant.listActions.gate.templateLink'),
                  path: '/settings/decree-templates',
                },
                {
                  icon: <Code className="h-4 w-4" aria-hidden="true" />,
                  label: t('tenant.listActions.gate.procedureLink'),
                  path: '/settings/decree-procedures',
                },
              ]}
            />
          </>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <Button variant="secondary" onClick={onClose}>
          {t('tenant.listActions.gate.closeLabel')}
        </Button>
      </div>
    </Modal>
  );
}

function Requirement({
  met,
  label,
  hint,
  links,
  onGo,
}: {
  met: boolean;
  label: string;
  hint?: string;
  links: ReqLink[];
  onGo: (path: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="py-3">
      <div className="flex items-center gap-2.5">
        {met ? (
          <CircleCheck className="h-4 w-4 shrink-0 text-text-success" aria-hidden="true" />
        ) : (
          <CircleX className="h-4 w-4 shrink-0 text-text-warning" aria-hidden="true" />
        )}
        <span className="flex-1 text-sm text-text-primary">
          {label}
          {hint ? <span className="ml-1.5 text-xs text-text-muted">— {hint}</span> : null}
        </span>
        <span className={`text-xs ${met ? 'text-text-success' : 'text-text-warning'}`}>
          {met ? t('tenant.listActions.gate.ready') : t('tenant.listActions.gate.missing')}
        </span>
      </div>
      {!met && (
        <div className="ml-6 mt-2 flex flex-col items-start gap-1">
          {links.map((l) => (
            <Button
              key={l.label}
              variant="ghost"
              onClick={() => onGo(l.path)}
              leftIcon={l.icon}
              rightIcon={<ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />}
              className="h-auto px-1 py-0.5 text-sm text-accent-600 hover:bg-transparent hover:underline"
            >
              {l.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
